<div class="modal-content upsert-service-schedule-modal">
	<div class="modal-header">
		<h5 class="modal-title">{{ modalTitle }}</h5>
		<button type="button" class="btn-close" [attr.aria-label]="'common.close' | transloco" (click)="closeModal()"></button>
	</div>
	<div class="modal-body">
		@if(isInitializing) {
			<div class="d-flex align-items-center justify-content-center">
				<span class="spinner-border"></span>
			</div>
		} @else {
			<form [formGroup]="scheduleForm">
				<div class="form-fields">
					<!-- Account -->
					<div class="field-group">
						<app-account-selector (accountChanged)="onSelectAccount($event)" [isFilter]="false"
							[positionOffset]="'-90,15'" [preSelectedAccountId]="preSelectedAccountId"></app-account-selector>
					</div>

					<!-- Asset Account -->
					<div class="field-group asset-account-field">
						<label class="form-label">{{ 'serviceLibrary.upsert.assetAccount' | transloco }} <span class="required">*</span></label>
						<app-asset-assignment-filter
							[isLoaded]="assetsLoaded"
							[filter]="assetFilter"
							[tree]="assetsTree"
							[entityType]="'asset'"
							[labelTitle]="'serviceLibrary.upsert.selectAssets' | transloco"
							[preSelectedIds]="preSelectedAssetIds"
							(driverAssignmentFilterChanged)="onAssetFilterChanged($event)">
						</app-asset-assignment-filter>
					</div>

					<!-- Name -->
					<div class="field-group">
						<label class="form-label">{{ 'serviceLibrary.upsert.name' | transloco }} <span class="required">*</span></label>
						<input type="text" formControlName="name" class="form-control" [placeholder]="'serviceLibrary.upsert.namePlaceholder' | transloco" (input)="clearCustomNameError()">
						<app-form-error-message [control]="scheduleForm.get('name')!" [errorMessage]="getErrorMessage('name')"
							[formSubmitted]="submitted">
						</app-form-error-message>
					</div>

					<!-- Service Groups -->
					<div formArrayName="serviceIntervalGroups">
						@for (group of serviceIntervalGroups; track group; let i = $index) {
							<div class="service-group-card" [formGroupName]="i">
								<!-- Service Type Header Row -->
								<div class="service-group-header">
									<div class="field-group" style="flex: 1;">
										<label class="form-label">{{ 'serviceLibrary.upsert.serviceType' | transloco }} <span class="required">*</span></label>
										<span ngbDropdown class="filter-dropdown" [autoClose]="true">
											<button
												type="button"
												class="btn category-filter form-control dropdown-toggle filter-input"
												[id]="'serviceTypeDropdown_' + i"
												[disabled]="scheduleForm.disabled"
												ngbDropdownToggle>
												<div class="d-flex flex-row justify-content-between align-items-center">
													<span class="align-middle">{{ getSelectedServiceTypeLabel(i) }}</span>
													<i class="ph ph-caret-down lh-base"></i>
												</div>
											</button>
											<div ngbDropdownMenu [attr.aria-labelledby]="'serviceTypeDropdown_' + i" class="dropdown-menu-custom">
												@for (type of getAvailableServiceTypes(i); track type.id) {
													<button
														class="btn btn-outline dropdown-item-custom"
														(click)="onServiceTypeSelect(i, type)"
														ngbDropdownItem
														type="button">
														{{ serviceTypeLabel(type) }}
													</button>
												}
											</div>
										</span>
									</div>
									@if (i > 0) {
										<button type="button" class="btn remove-group-btn" (click)="removeServiceGroup(i)">
											<i class="ph ph-trash"></i>
										</button>
									}
								</div>

								<!-- Interval Type & Interval Row -->
								<div class="field-group-row">
									<div class="field-group field-half">
										<label class="form-label">{{ 'serviceLibrary.upsert.intervalType' | transloco }} <span class="required">*</span></label>
										<span ngbDropdown class="filter-dropdown" [autoClose]="true">
											<button
												type="button"
												class="btn category-filter form-control dropdown-toggle filter-input"
												[id]="'intervalTypeDropdown_' + i"
												[disabled]="scheduleForm.disabled"
												ngbDropdownToggle>
												<div class="d-flex flex-row justify-content-between align-items-center">
													<span class="align-middle">{{ getSelectedIntervalTypeLabel(i) }}</span>
													<i class="ph ph-caret-down lh-base"></i>
												</div>
											</button>
											<div ngbDropdownMenu [attr.aria-labelledby]="'intervalTypeDropdown_' + i" class="dropdown-menu-custom">
												@for (type of intervalTypes; track type.id) {
													<button
														class="btn btn-outline dropdown-item-custom"
														(click)="onIntervalTypeSelect(i, type)"
														ngbDropdownItem
														type="button">
														{{ intervalTypeLabel(type) }}
													</button>
												}
											</div>
										</span>
									</div>
									<div class="field-group field-half">
										<label class="form-label">{{ 'serviceLibrary.upsert.interval' | transloco }} <span class="required">*</span></label>
										<div class="interval-input-wrapper">
											<input type="text" formControlName="interval" class="form-control" [placeholder]="'serviceLibrary.upsert.intervalPlaceholder' | transloco">
											@if (getIntervalSuffix(i)) {
												<span class="interval-suffix">{{ getIntervalSuffix(i) }}</span>
											}
										</div>
									</div>
								</div>

								<!-- Part Required -->
								<div class="field-group">
									<label class="form-label">{{ 'serviceLibrary.upsert.partRequired' | transloco }}</label>
									<div class="parts-display-area" (click)="openSelectPartsModal(i)">
										<div class="chips-wrapper">
											@if (!isPartTypesLoading && serviceIntervalGroups[i].selectedPartTypes.length === 0) {
												<span class="placeholder-text">{{ 'serviceLibrary.upsert.selectRequiredParts' | transloco }}</span>
											}
											@if (isPartTypesLoading) {
												<span class="placeholder-text">
													{{ 'maintenanceOverview.modals.selectParts.loadingParts' | transloco }}
												</span>
											}
											@for (partType of serviceIntervalGroups[i].selectedPartTypes; track partType.id) {
												<span class="part-chip">
													{{ partType.name }}
													<button type="button" class="chip-remove" (click)="removePart(i, partType.id, $event)">
														<i class="ph ph-x"></i>
													</button>
												</span>
											}
											<button type="button" class="part-chip chip-add" [attr.aria-label]="'serviceLibrary.upsert.addRequiredPart' | transloco" (click)="openSelectPartsModal(i); $event.stopPropagation()">
												<i class="ph ph-plus"></i>
											</button>
										</div>
									</div>
								</div>

								<!-- est downtime -->
								<div class="field-group">
									<label class="form-label">{{ 'serviceLibrary.upsert.estimatedDowntime' | transloco }}</label>
									<input type="text" formControlName="estDownTime" class="form-control" [placeholder]="'serviceLibrary.upsert.estimatedDowntimePlaceholder' | transloco">
								</div>

								<!-- Alert Notification -->
								<!-- <div class="field-group alert-notification-group">
									<label class="form-label section-label">Alert notification</label>
									<div class="alert-toggle-row">
										<div class="alert-toggle-info">
											<span class="alert-toggle-title">Enable email alerts</span>
											<span class="alert-toggle-description">Send email notification when service is due.</span>
										</div>
										<div class="toggle-switch" [class.active]="group.enableEmailAlerts" (click)="onToggleEmailAlerts(i)">
											<div class="toggle-knob"></div>
										</div>
									</div>
								</div> -->
							</div>
						}
					</div>

				<!-- Add -->
					<div class="add-more-section">
						<button type="button" class="btn add-more-btn" (click)="addServiceIntervalGroup()"
							[disabled]="serviceIntervalGroups.length >= serviceTypes.length">
							<i class="ph ph-plus"></i>
							<span>{{ 'serviceLibrary.upsert.add' | transloco }}</span>
						</button>
					</div>
				</div>

				<div class="action-buttons">
					<button type="button" class="btn btn-outline-danger cancel-btn" (click)="closeModal()">{{ 'common.cancel' | transloco }}</button>
					<button type="button"
						class="btn btn-danger d-flex justify-content-center align-items-center gap-2 save-btn"
						[disabled]="!selectedAccount || scheduleForm.invalid || isLoading"
						(click)="onSubmit()">
						@if (isLoading) {
							<span class="spinner-border"></span>
						} @else {
							<span>{{ (isEditMode ? 'serviceLibrary.upsert.update' : 'assetShared.createMaintenance.create') | transloco }}</span>
						}
					</button>
				</div>
			</form>
		}
	</div>
</div>
